const STORAGE_KEY = "ickonic.signup.affiliate";
const CODE_PATTERN = /^[A-Za-z0-9_.-]{1,128}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface AffiliateReferral {
    code: string;
    expiresAt: number;
    promotekitReferral?: string;
}

let memoryReferral: AffiliateReferral | null = null;
let persistenceFailed = false;

function retentionDays(): number {
    const configured = Number(process.env.REACT_APP_AFFILIATE_RETENTION_DAYS);
    return Number.isFinite(configured) && configured > 0 ? configured : 60;
}

function save(referral: AffiliateReferral | null): void {
    memoryReferral = referral;
    try {
        if (referral) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(referral));
        else window.localStorage.removeItem(STORAGE_KEY);
        persistenceFailed = false;
    } catch {
        persistenceFailed = true;
        // Keep navigation working when persistent browser storage is disabled.
    }
}

export function clearAffiliateReferral(removeFromUrl = false): void {
    save(null);
    if (removeFromUrl && typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        params.delete("via");
        const query = params.toString();
        window.history.replaceState(window.history.state, "", window.location.pathname +
            (query ? "?" + query : "") + window.location.hash);
    }
}

/** First-touch attribution, separate from temporary billing promotion codes. */
export function getAffiliateReferral(search?: string, now = Date.now()): AffiliateReferral | null {
    if (typeof window === "undefined") return null;
    let referral = memoryReferral;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        referral = raw ? JSON.parse(raw) : persistenceFailed ? memoryReferral : null;
    } catch {
        // Use the in-memory value when storage is inaccessible.
    }
    if (!referral || typeof referral.code !== "string" || !CODE_PATTERN.test(referral.code) ||
        !Number.isFinite(referral.expiresAt) || referral.expiresAt <= now) {
        referral = null;
        save(null);
    }

    const code = new URLSearchParams(search ?? window.location.search).get("via")?.trim();
    if (!referral && code && CODE_PATTERN.test(code)) {
        referral = {code, expiresAt: now + retentionDays() * DAY_MS};
        save(referral);
    }

    const token = window.promotekit_referral;
    if (referral && typeof token === "string" && CODE_PATTERN.test(token) &&
        referral.promotekitReferral !== token) {
        referral = {...referral, promotekitReferral: token};
        save(referral);
    }
    return referral;
}

/** Public organization ID only; never put a PromoteKit API key in the frontend. */
export function loadPromoteKitScript(): void {
    const organizationId = process.env.REACT_APP_PROMOTEKIT_ID?.trim();
    if (!organizationId || typeof document === "undefined" ||
        document.querySelector("script[data-promotekit]")) return;

    // Official SDK: https://cdn.promotekit.com/promotekit.js
    const script = document.createElement("script");
    script.src = "https://cdn.promotekit.com/promotekit.js";
    script.async = true;
    script.setAttribute("data-promotekit", organizationId);
    script.addEventListener("load", () => getAffiliateReferral());
    document.head.appendChild(script);
}

/** Keep the saved first-touch code on signup links without forwarding unrelated landing parameters. */
export function withAffiliateReferral(url: string): string {
    const hashIndex = url.indexOf("#");
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
    const withoutHash = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const queryIndex = withoutHash.indexOf("?");
    const path = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
    if (path !== "/sign-up" && path !== "/sign-up/") return url;
    const referral = getAffiliateReferral();
    if (!referral) return url;
    const params = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "");
    params.set("via", referral.code);
    return path + "?" + params.toString() + hash;
}
